import React, { useCallback, useEffect, useRef } from 'react';
interface ElectricBorderProps {
  children: React.ReactNode;
  color?: string;
  speed?: number;
  chaos?: number;
  borderRadius?: number;
  className?: string;
  style?: React.CSSProperties;
}
export function ElectricBorder({
  children,
  color = '#c9a84c',
  speed = 1,
  chaos = 0.12,
  borderRadius = 24,
  className,
  style
}: ElectricBorderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number>(0);
  const timeRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const random = useCallback((x: number) => {
    return Math.sin(x * 12.9898) * 43758.5453 % 1;
  }, []);
  const noise2D = useCallback((x: number, y: number) => {
    const i = Math.floor(x);
    const j = Math.floor(y);
    const fx = x - i;
    const fy = y - j;
    const a = random(i + j * 57);
    const b = random(i + 1 + j * 57);
    const c = random(i + (j + 1) * 57);
    const d = random(i + 1 + (j + 1) * 57);
    const ux = fx * fx * (3.0 - 2.0 * fx);
    const uy = fy * fy * (3.0 - 2.0 * fy);
    return a * (1 - ux) * (1 - uy) + b * ux * (1 - uy) + c * (1 - ux) * uy + d * ux * uy;
  }, [random]);
  const octavedNoise = useCallback((x: number, octaves: number, lacunarity: number, gain: number, baseAmplitude: number, baseFrequency: number, time: number, seed: number, baseFlatness: number) => {
    let y = 0;
    let amplitude = baseAmplitude;
    let frequency = baseFrequency;
    for (let i = 0; i < octaves; i++) {
      let octaveAmplitude = amplitude;
      if (i === 0) {
        octaveAmplitude *= baseFlatness;
      }
      y += octaveAmplitude * noise2D(frequency * x + seed * 100, time * frequency * 0.3);
      frequency *= lacunarity;
      amplitude *= gain;
    }
    return y;
  }, [noise2D]);
  const getCornerPoint = useCallback((centerX: number, centerY: number, radius: number, startAngle: number, arcLength: number, progress: number) => {
    const angle = startAngle + progress * arcLength;
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle)
    };
  }, []);
  const getRoundedRectPoint = useCallback((t: number, left: number, top: number, width: number, height: number, radius: number) => {
    const straightWidth = width - 2 * radius;
    const straightHeight = height - 2 * radius;
    const cornerArc = Math.PI * radius / 2;
    const totalPerimeter = 2 * straightWidth + 2 * straightHeight + 4 * cornerArc;
    const distance = t * totalPerimeter;
    let accumulated = 0;
    if (distance <= accumulated + straightWidth) {
      const progress = (distance - accumulated) / straightWidth;
      return {
        x: left + radius + progress * straightWidth,
        y: top
      };
    }
    accumulated += straightWidth;
    if (distance <= accumulated + cornerArc) {
      const progress = (distance - accumulated) / cornerArc;
      return getCornerPoint(left + width - radius, top + radius, radius, -Math.PI / 2, Math.PI / 2, progress);
    }
    accumulated += cornerArc;
    if (distance <= accumulated + straightHeight) {
      const progress = (distance - accumulated) / straightHeight;
      return {
        x: left + width,
        y: top + radius + progress * straightHeight
      };
    }
    accumulated += straightHeight;
    if (distance <= accumulated + cornerArc) {
      const progress = (distance - accumulated) / cornerArc;
      return getCornerPoint(left + width - radius, top + height - radius, radius, 0, Math.PI / 2, progress);
    }
    accumulated += cornerArc;
    if (distance <= accumulated + straightWidth) {
      const progress = (distance - accumulated) / straightWidth;
      return {
        x: left + width - radius - progress * straightWidth,
        y: top + height
      };
    }
    accumulated += straightWidth;
    if (distance <= accumulated + cornerArc) {
      const progress = (distance - accumulated) / cornerArc;
      return getCornerPoint(left + radius, top + height - radius, radius, Math.PI / 2, Math.PI / 2, progress);
    }
    accumulated += cornerArc;
    if (distance <= accumulated + straightHeight) {
      const progress = (distance - accumulated) / straightHeight;
      return {
        x: left,
        y: top + height - radius - progress * straightHeight
      };
    }
    accumulated += straightHeight;
    const progress = (distance - accumulated) / cornerArc;
    return getCornerPoint(left + radius, top + radius, radius, Math.PI, Math.PI / 2, progress);
  }, [getCornerPoint]);
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const octaves = 10;
    const lacunarity = 1.6;
    const gain = 0.7;
    const amplitude = chaos;
    const frequency = 10;
    const baseFlatness = 0;
    const displacement = 60;
    const borderOffset = 60;
    const updateSize = () => {
      const rect = container.getBoundingClientRect();
      const width = rect.width + borderOffset * 2;
      const height = rect.height + borderOffset * 2;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
      return {
        width,
        height
      };
    };
    let {
      width,
      height
    } = updateSize();
    const drawElectricBorder = (currentTime: number) => {
      if (!canvas || !ctx) return;
      const deltaTime = (currentTime - lastFrameTimeRef.current) / 1000;
      timeRef.current += deltaTime * speed;
      lastFrameTimeRef.current = currentTime;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      const scale = displacement;
      const left = borderOffset;
      const top = borderOffset;
      const borderWidth = width - 2 * borderOffset;
      const borderHeight = height - 2 * borderOffset;
      const maxRadius = Math.min(borderWidth, borderHeight) / 2;
      const rad = Math.min(borderRadius, maxRadius);
      const approximatePerimeter = 2 * (borderWidth + borderHeight) + 2 * Math.PI * rad;
      const sampleCount = Math.floor(approximatePerimeter / 2);
      ctx.beginPath();
      for (let i = 0; i <= sampleCount; i++) {
        const progress = i / sampleCount;
        const point = getRoundedRectPoint(progress, left, top, borderWidth, borderHeight, rad);
        const xNoise = octavedNoise(progress * 8, octaves, lacunarity, gain, amplitude, frequency, timeRef.current, 0, baseFlatness);
        const yNoise = octavedNoise(progress * 8, octaves, lacunarity, gain, amplitude, frequency, timeRef.current, 1, baseFlatness);
        const displacedX = point.x + xNoise * scale;
        const displacedY = point.y + yNoise * scale;
        if (i === 0) {
          ctx.moveTo(displacedX, displacedY);
        } else {
          ctx.lineTo(displacedX, displacedY);
        }
      }
      ctx.closePath();
      ctx.stroke();
      animationRef.current = requestAnimationFrame(drawElectricBorder);
    };
    const resizeObserver = new ResizeObserver(() => {
      const newSize = updateSize();
      width = newSize.width;
      height = newSize.height;
    });
    resizeObserver.observe(container);
    animationRef.current = requestAnimationFrame(drawElectricBorder);
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [color, speed, chaos, borderRadius, octavedNoise, getRoundedRectPoint]);
  const vars: Record<string, string | number> = {
    '--electric-border-color': color,
    borderRadius
  };
  return <div ref={containerRef} className={`electric-border ${className ?? ''}`} style={{
    ...vars,
    ...style,
    position: 'relative'
  } as React.CSSProperties} data-id="element-237">
      {/* Content goes BEHIND the electric layers */}
      <div style={{
      position: 'relative',
      zIndex: 1
    }} data-id="element-238">
        {children}
      </div>

      {/* Canvas - IN FRONT of content, pointer-events none so card is clickable */}
      <div className="eb-canvas-container" style={{
      position: 'absolute',
      top: '-60px',
      left: '-60px',
      right: '-60px',
      bottom: '-60px',
      pointerEvents: 'none',
      zIndex: 2,
      overflow: 'visible'
    }} data-id="element-239">
        <canvas ref={canvasRef} style={{
        display: 'block'
      }} data-id="element-240" />
      </div>

      {/* Glow layers - IN FRONT */}
      <div className="eb-layers" style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 2,
      borderRadius,
      overflow: 'visible'
    }} data-id="element-241">
        <div className="eb-glow-1" style={{
        position: 'absolute',
        inset: '-2px',
        borderRadius: borderRadius + 2,
        background: `linear-gradient(135deg, ${color}33, transparent, ${color}22)`,
        filter: 'blur(8px)',
        pointerEvents: 'none'
      }} data-id="element-242" />
        <div className="eb-glow-2" style={{
        position: 'absolute',
        inset: '-4px',
        borderRadius: borderRadius + 4,
        background: `radial-gradient(ellipse at center, ${color}15, transparent 70%)`,
        filter: 'blur(20px)',
        pointerEvents: 'none'
      }} data-id="element-243" />
        <div className="eb-background-glow" style={{
        position: 'absolute',
        inset: '-8px',
        borderRadius: borderRadius + 8,
        background: `radial-gradient(ellipse at center, ${color}0a, transparent 60%)`,
        filter: 'blur(30px)',
        pointerEvents: 'none'
      }} data-id="element-244" />
      </div>
    </div>;
}